import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Upload, Download, Trash2, Search, CheckCircle2, FileText, Edit2, Check, X, Plus, ArrowUp, ArrowDown, MessageCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StaffFormsDetailsTab — Employee × Form-Column matrix.
 *
 * Columns are driven by a separate `hr_form_columns` collection (NOT the
 * Form Templates Library). Auto-seeds 29 canonical Staff-Form columns on
 * first load. HR can rename / reorder / add / delete columns inline.
 */
const StaffFormsDetailsTab = () => {
  const [employees, setEmployees] = useState([]);
  const [columns, setColumns] = useState([]);
  const [filledForms, setFilledForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);
  const [editColumnsMode, setEditColumnsMode] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [newColumnName, setNewColumnName] = useState('');
  const [editingMobileId, setEditingMobileId] = useState(null);
  const [mobileDraft, setMobileDraft] = useState('');

  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, colsRes, formsRes] = await Promise.all([
        axios.get(`${API}/hr/attendance-employees`, { headers }),
        axios.get(`${API}/hr/form-columns`, { headers }),
        axios.get(`${API}/hr/employee-forms`, { headers }),
      ]);
      setEmployees(empRes.data.employees || []);
      setColumns(colsRes.data.columns || []);
      setFilledForms(formsRes.data.forms || []);
    } catch {
      toast.error('Failed to load Staff Forms Details');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filledMap = useMemo(() => {
    const m = {};
    filledForms.forEach(f => {
      if (f.columnId) m[`${f.employeeId}::${f.columnId}`] = f;
    });
    return m;
  }, [filledForms]);

  const filteredEmps = employees.filter(e =>
    (e.employeeName || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.department || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.designation || '').toLowerCase().includes(search.toLowerCase())
  );

  // ---- File upload / delete ----
  const handleUpload = async (employeeId, columnId, file) => {
    if (!file) return;
    const key = `${employeeId}-${columnId}`;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('column_id', columnId);
      fd.append('file', file);
      await axios.post(`${API}/hr/employee-forms/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      const formsRes = await axios.get(`${API}/hr/employee-forms`, { headers });
      setFilledForms(formsRes.data.forms || []);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Delete this filled form?')) return;
    try {
      await axios.delete(`${API}/hr/employee-forms/${formId}`, { headers });
      setFilledForms(prev => prev.filter(f => f.id !== formId));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // ---- Column edit ops ----
  const saveRename = async (colId) => {
    const name = renameDraft.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      await axios.patch(`${API}/hr/form-columns/${colId}`, { name }, { headers });
      setColumns(prev => prev.map(c => (c.id === colId ? { ...c, name } : c)));
      toast.success('Renamed');
    } catch {
      toast.error('Rename failed');
    } finally {
      setRenamingId(null);
    }
  };

  const moveColumn = async (idx, dir) => {
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= columns.length) return;
    const next = [...columns];
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    setColumns(next);
    try {
      await axios.put(`${API}/hr/form-columns/reorder`, { ids: next.map(c => c.id) }, { headers });
    } catch {
      toast.error('Reorder failed');
      fetchAll();
    }
  };

  const addColumn = async () => {
    const name = newColumnName.trim();
    if (!name) { toast.error('Enter a column name'); return; }
    try {
      const res = await axios.post(`${API}/hr/form-columns`, { name }, { headers });
      setColumns(prev => [...prev, res.data.column]);
      setNewColumnName('');
      toast.success('Column added');
    } catch {
      toast.error('Add failed');
    }
  };

  const removeColumn = async (colId, colName) => {
    if (!window.confirm(`Delete column "${colName}"? All filled forms in this column will also be removed.`)) return;
    try {
      await axios.delete(`${API}/hr/form-columns/${colId}`, { headers });
      setColumns(prev => prev.filter(c => c.id !== colId));
      setFilledForms(prev => prev.filter(f => f.columnId !== colId));
      toast.success('Column deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // ---- Mobile inline edit ----
  const saveMobile = async (empId) => {
    const m = mobileDraft.replace(/\D/g, '').slice(-10);
    setEditingMobileId(null);
    try {
      await axios.patch(`${API}/hr/attendance-employees/${empId}/mobile`, { mobile: m }, { headers });
      setEmployees(prev => prev.map(e => (e.employeeId === empId ? { ...e, mobile: m } : e)));
      if (m) toast.success('Mobile saved');
    } catch {
      toast.error('Failed to save mobile');
    }
  };

  // ---- WhatsApp helpers ----
  const completionFor = (empId) => {
    const filledIds = new Set(filledForms.filter(f => f.employeeId === empId).map(f => f.columnId));
    const done = columns.filter(c => filledIds.has(c.id)).length;
    return { done, total: columns.length };
  };

  const openWhatsAppReminder = (emp) => {
    const mobile = (emp.mobile || '').replace(/\D/g, '').slice(-10);
    if (!mobile || mobile.length !== 10) {
      toast.error('Add a valid 10-digit mobile first');
      return;
    }
    const filledIds = new Set(filledForms.filter(f => f.employeeId === emp.employeeId).map(f => f.columnId));
    const missing = columns.filter(c => !filledIds.has(c.id));
    const lines = missing.length
      ? missing.slice(0, 15).map(c => `• ${c.name}`).join('\n')
      : '(All forms submitted — thank you!)';
    const more = missing.length > 15 ? `\n…and ${missing.length - 15} more` : '';
    const msg = `Hi ${emp.employeeName.split(' ')[0]},\n\nPlease submit the following pending HR forms at your earliest:\n\n${lines}${more}\n\n— DBK HR Team`;
    const url = `https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const totalRequired = employees.length * columns.length;
  const totalFilled = filledForms.filter(f => columns.some(c => c.id === f.columnId)).length;
  const completion = totalRequired > 0 ? Math.round((totalFilled / totalRequired) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="staff-forms-details-tab">
      {/* KPI bar + search + edit toggle */}
      <div className="flex flex-wrap items-center gap-3 bg-stone-50 border border-stone-200 rounded-lg p-3">
        <div className="flex-1 min-w-[200px] flex items-center gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-stone-500">Employees</div>
            <div className="text-lg font-bold text-stone-800">{employees.length}</div>
          </div>
          <div className="h-8 w-px bg-stone-300" />
          <div>
            <div className="text-[10px] uppercase tracking-wide text-stone-500">Form Columns</div>
            <div className="text-lg font-bold text-stone-800">{columns.length}</div>
          </div>
          <div className="h-8 w-px bg-stone-300" />
          <div>
            <div className="text-[10px] uppercase tracking-wide text-stone-500">Filled</div>
            <div className="text-lg font-bold text-emerald-700">{totalFilled} / {totalRequired}</div>
          </div>
          <div className="h-8 w-px bg-stone-300" />
          <div className="flex items-center gap-2">
            <div className="w-24 bg-stone-200 rounded-full h-2 overflow-hidden">
              <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${completion}%` }} />
            </div>
            <span className="text-sm font-bold text-emerald-700">{completion}%</span>
          </div>
        </div>
        <div className="relative w-56">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-stone-400" />
          <Input
            data-testid="staff-forms-details-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search employee / dept / role..."
            className="pl-8 h-9 text-xs"
          />
        </div>
        <button
          data-testid="edit-columns-toggle"
          onClick={() => setEditColumnsMode(m => !m)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold transition-all ${editColumnsMode ? 'bg-emerald-600 text-white' : 'bg-stone-800 text-white hover:bg-stone-700'}`}
        >
          {editColumnsMode ? <><Check className="w-3 h-3" />Done</> : <><Edit2 className="w-3 h-3" />Edit Columns</>}
        </button>
      </div>

      {/* Add new column row (only in edit mode) */}
      {editColumnsMode && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded p-2">
          <span className="text-xs font-semibold text-amber-800">Add new column:</span>
          <Input
            data-testid="new-column-input"
            value={newColumnName}
            onChange={e => setNewColumnName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addColumn()}
            placeholder="e.g. 30. STAFF SUPP DOC"
            className="h-7 text-xs flex-1 max-w-xs bg-white"
          />
          <button
            data-testid="add-column-btn"
            onClick={addColumn}
            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
          <span className="text-[10px] text-stone-500 ml-2">Tip: drag arrows to reorder, click name to rename, × to delete.</span>
        </div>
      )}

      {/* Matrix */}
      <Card>
        <CardContent className="p-0">
          <div
            className="overflow-auto border border-stone-200 rounded"
            style={{ maxHeight: 620 }}
            data-testid="staff-forms-details-scroll"
          >
            <table className="text-[11px] border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-20">
                <tr className="bg-emerald-700 text-white">
                  <th className="px-2 py-2 text-center font-bold border border-emerald-900 sticky left-0 z-30 bg-emerald-700" style={{ width: 42 }}>SR</th>
                  <th className="px-2 py-2 text-left font-bold border border-emerald-900 sticky z-30 bg-emerald-700" style={{ width: 180, left: 42 }}>EMPLOYEE NAME</th>
                  <th className="px-2 py-2 text-left font-bold border border-emerald-900" style={{ width: 110 }}>DEPT</th>
                  <th className="px-2 py-2 text-left font-bold border border-emerald-900" style={{ width: 130 }}>DESIGNATION</th>
                  <th className="px-2 py-2 text-center font-bold border border-emerald-900" style={{ width: 165 }}>STATUS · WHATSAPP</th>
                  {columns.map((c, idx) => (
                    <th
                      key={c.id}
                      className="px-1 py-2 text-center font-bold border border-emerald-900 align-bottom"
                      style={{ width: 95, height: editColumnsMode ? 180 : 150 }}
                      title={c.name}
                    >
                      {editColumnsMode && (
                        <div className="flex items-center justify-center gap-0.5 mb-1.5">
                          <button
                            onClick={() => moveColumn(idx, -1)}
                            disabled={idx === 0}
                            className="p-0.5 rounded bg-white/10 hover:bg-white/30 disabled:opacity-30"
                            title="Move left"
                          >
                            <ArrowUp className="w-2.5 h-2.5 -rotate-90" />
                          </button>
                          <button
                            onClick={() => moveColumn(idx, 1)}
                            disabled={idx === columns.length - 1}
                            className="p-0.5 rounded bg-white/10 hover:bg-white/30 disabled:opacity-30"
                            title="Move right"
                          >
                            <ArrowDown className="w-2.5 h-2.5 -rotate-90" />
                          </button>
                          <button
                            onClick={() => removeColumn(c.id, c.name)}
                            className="p-0.5 rounded bg-red-500/30 hover:bg-red-600 ml-0.5"
                            title="Delete column"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                      {renamingId === c.id ? (
                        <div className="flex flex-col items-center gap-1">
                          <input
                            value={renameDraft}
                            onChange={e => setRenameDraft(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveRename(c.id);
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            onBlur={() => saveRename(c.id)}
                            autoFocus
                            className="text-[10px] text-stone-900 px-1 py-0.5 rounded w-20 bg-white"
                          />
                        </div>
                      ) : (
                        <div
                          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap', margin: '0 auto', cursor: editColumnsMode ? 'pointer' : 'default' }}
                          onClick={() => editColumnsMode && (setRenamingId(c.id), setRenameDraft(c.name))}
                          title={editColumnsMode ? 'Click to rename' : c.name}
                        >
                          {c.name.slice(0, 36)}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5 + columns.length} className="py-8 text-center text-stone-400">Loading…</td></tr>
                )}
                {!loading && filteredEmps.length === 0 && (
                  <tr><td colSpan={5 + columns.length} className="py-8 text-center text-stone-400">No employees match.</td></tr>
                )}
                {!loading && filteredEmps.map((emp, i) => {
                  const rowBg = i % 2 === 0 ? '#ffffff' : '#fafaf9';
                  return (
                    <tr key={emp.employeeId} className="hover:bg-emerald-50/40">
                      <td className="px-2 py-1 text-center font-semibold border border-stone-200 sticky left-0 z-10" style={{ backgroundColor: rowBg, width: 42 }}>{emp.srNo || i + 1}</td>
                      <td className="px-2 py-1 text-left font-bold border border-stone-200 sticky z-10 truncate" style={{ left: 42, backgroundColor: rowBg, width: 180, maxWidth: 180 }} title={emp.employeeName}>
                        {emp.employeeName}
                        {emp.isJoinee && <span className="ml-1 text-[9px] bg-amber-200 text-amber-900 rounded px-1 py-0.5 font-semibold">NEW</span>}
                      </td>
                      <td className="px-2 py-1 text-left text-stone-600 border border-stone-200 truncate" style={{ width: 110, maxWidth: 110 }}>{emp.department || ''}</td>
                      <td className="px-2 py-1 text-left text-stone-600 border border-stone-200 truncate" style={{ width: 130, maxWidth: 130 }}>{emp.designation || ''}</td>
                      {(() => {
                        const { done, total } = completionFor(emp.employeeId);
                        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                        const tone = pct >= 80 ? { bg: '#dcfce7', text: '#166534', bar: '#16a34a' }
                          : pct >= 40 ? { bg: '#fef9c3', text: '#854d0e', bar: '#ca8a04' }
                          : { bg: '#fee2e2', text: '#991b1b', bar: '#dc2626' };
                        const mobileStr = (emp.mobile || '').replace(/\D/g, '').slice(-10);
                        const hasValidMobile = mobileStr.length === 10;
                        const isEditing = editingMobileId === emp.employeeId;
                        return (
                          <td className="border border-stone-200 px-1.5 py-1 align-middle" style={{ width: 165, backgroundColor: rowBg }}>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center justify-between gap-1">
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0"
                                  style={{ backgroundColor: tone.bg, color: tone.text }}
                                  data-testid={`compliance-${emp.employeeId}`}
                                  title={`${done} of ${total} forms submitted`}
                                >
                                  {done}/{total} · {pct}%
                                </span>
                                <button
                                  onClick={() => openWhatsAppReminder(emp)}
                                  disabled={!hasValidMobile}
                                  data-testid={`whatsapp-${emp.employeeId}`}
                                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all ${hasValidMobile ? 'bg-[#25D366] hover:bg-[#1da851] text-white' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
                                  title={hasValidMobile ? `Send WhatsApp reminder to ${emp.employeeName}` : 'Add a 10-digit mobile first'}
                                >
                                  <MessageCircle className="w-3 h-3" />
                                  WA
                                </button>
                              </div>
                              <div className="w-full bg-white rounded-full overflow-hidden" style={{ height: 3 }}>
                                <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: tone.bar }} />
                              </div>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  value={mobileDraft}
                                  onChange={e => setMobileDraft(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                  onBlur={() => saveMobile(emp.employeeId)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveMobile(emp.employeeId);
                                    if (e.key === 'Escape') setEditingMobileId(null);
                                  }}
                                  placeholder="10-digit mobile"
                                  className="text-[10px] px-1 py-0.5 border border-emerald-400 rounded w-full"
                                  data-testid={`mobile-input-${emp.employeeId}`}
                                />
                              ) : (
                                <button
                                  onClick={() => { setEditingMobileId(emp.employeeId); setMobileDraft(mobileStr); }}
                                  data-testid={`mobile-edit-${emp.employeeId}`}
                                  className="inline-flex items-center gap-1 text-[9px] text-stone-500 hover:text-emerald-700 hover:underline"
                                  title="Click to add / edit mobile"
                                >
                                  <Phone className="w-2.5 h-2.5" />
                                  {mobileStr ? `+91 ${mobileStr}` : 'Add mobile'}
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })()}
                      {columns.map(c => {
                        const filled = filledMap[`${emp.employeeId}::${c.id}`];
                        const cellKey = `${emp.employeeId}-${c.id}`;
                        const isUploading = uploadingKey === cellKey;
                        return (
                          <td key={c.id} className="border border-stone-200 p-1 align-middle text-center" style={{ width: 95, backgroundColor: filled ? '#dcfce7' : 'transparent' }}>
                            {isUploading ? (
                              <span className="text-[10px] text-stone-500">Uploading…</span>
                            ) : filled ? (
                              <div className="flex items-center justify-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                <a
                                  href={`${API}/hr/employee-forms/${filled.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`download-${cellKey}`}
                                  className="p-0.5 rounded hover:bg-emerald-200 text-emerald-800"
                                  title={filled.name}
                                >
                                  <Download className="w-3 h-3" />
                                </a>
                                <button
                                  onClick={() => handleDelete(filled.id)}
                                  data-testid={`delete-${cellKey}`}
                                  className="p-0.5 rounded hover:bg-red-100 text-red-600"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                                <label className="p-0.5 rounded hover:bg-blue-100 text-blue-600 cursor-pointer" title="Replace">
                                  <Upload className="w-3 h-3" />
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={e => e.target.files?.[0] && handleUpload(emp.employeeId, c.id, e.target.files[0])}
                                  />
                                </label>
                              </div>
                            ) : (
                              <label
                                data-testid={`upload-${cellKey}`}
                                className="inline-flex items-center justify-center gap-1 text-[10px] text-stone-500 hover:text-emerald-700 hover:bg-emerald-50 cursor-pointer rounded px-1.5 py-1 border border-dashed border-stone-300 hover:border-emerald-400"
                              >
                                <Upload className="w-3 h-3" />
                                Upload
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={e => e.target.files?.[0] && handleUpload(emp.employeeId, c.id, e.target.files[0])}
                                />
                              </label>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {!loading && columns.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-stone-500">
            <FileText className="w-10 h-10 mx-auto mb-3 text-stone-300" />
            <p className="font-semibold mb-1">No form columns yet</p>
            <p className="text-sm">Click <span className="font-semibold text-emerald-700">Edit Columns</span> above to add some.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffFormsDetailsTab;
